import { fetchPaginated } from '@/lib/supabase-utils';
import {
  getLastScreenshotByTimeLogId,
  getReportableTimeLogIntervals,
  type ReportableIntervalOptions,
  type ScreenshotTimeEvidence,
  type TimeLogForMerge,
} from '@/lib/time-utils';

async function fetchCompleteEvidence<T>(query: any): Promise<T[]> {
  // Evidence cannot be inferred from a partial result or failed read.
  // Reuse pagination, but make failures explicit instead of returning a prefix.
  const maxRows = 50_000;
  const rows = await fetchPaginated<T>({
    range: async (start: number, end: number) => {
      const result = await query.range(start, end);
      if (result.error) throw result.error;
      return result;
    },
  }, 1000, maxRows);
  if (rows.length >= maxRows) throw new Error('Incomplete session evidence at row safety cap');
  return rows;
}

/**
 * Fetch duration evidence for the complete canonical sessions overlapping a
 * report. Capping must precede report-window clipping, so a screenshot just
 * outside that window can still determine the in-window duration.
 *
 * Queries use the caller's authenticated client/RLS and are bounded by known
 * reportable session IDs, owners and original timestamp bounds. Retained-image
 * metadata remains evidence. Callers must use a separate window-limited query
 * for gallery images, screenshot counts and activity summaries.
 * Legacy null-session metadata is fetched only for owners/bounds of sessions
 * lacking direct evidence. Neighboring sessions are checked for ambiguity,
 * including sessions starting outside the report window; they never add time
 * to the report or acquire ownership through a timestamp guess.
 */
export async function fetchSessionScreenshotEvidence(
  supabase: any,
  logs: TimeLogForMerge[],
  options: Omit<ReportableIntervalOptions, 'screenshotsBySession'> = {},
): Promise<ScreenshotTimeEvidence[]> {
  const nowMs = options.nowMs ?? Date.now();
  const reportableIds = new Set(getReportableTimeLogIntervals(logs, { ...options, nowMs })
    .map(interval => interval.logId));
  const sessions = [...new Map(logs.filter(log => log.id && reportableIds.has(log.id))
    .map(log => [log.id, log])).values()];
  const evidence: ScreenshotTimeEvidence[] = [];

  // Bound the IN-list size as well as each query's time span and pagination.
  for (let offset = 0; offset < sessions.length; offset += 100) {
    const batch = sessions.slice(offset, offset + 100);
    const startMs = Math.min(...batch.map(log => new Date(log.start_time).getTime()));
    const endMs = Math.max(...batch.map(log => log.end_time ? new Date(log.end_time).getTime() : nowMs));
    evidence.push(...await fetchCompleteEvidence<ScreenshotTimeEvidence>(
      supabase.from('screenshots')
        .select('user_id, time_log_id, captured_at')
        .in('time_log_id', batch.map(log => log.id!))
        .in('user_id', [...new Set(batch.map(log => log.user_id))])
        .gte('captured_at', new Date(startMs).toISOString())
        .lte('captured_at', new Date(endMs).toISOString())
        .order('captured_at')
        .order('id'),
    ));
  }

  const directCaps = getLastScreenshotByTimeLogId(logs, evidence, { ...options, nowMs });
  const missingByOwner = new Map<string, TimeLogForMerge[]>();
  for (const session of sessions) {
    if (directCaps.has(session.id!)) continue;
    const owned = missingByOwner.get(session.user_id) ?? [];
    owned.push(session);
    missingByOwner.set(session.user_id, owned);
  }

  for (const [owner, missing] of missingByOwner) {
    try {
      const startISO = new Date(Math.min(...missing.map(log => new Date(log.start_time).getTime()))).toISOString();
      const endISO = new Date(Math.max(...missing.map(log => log.end_time ? new Date(log.end_time).getTime() : nowMs))).toISOString();
      const legacy = await fetchCompleteEvidence<ScreenshotTimeEvidence>(
        supabase.from('screenshots')
          .select('user_id, time_log_id, captured_at')
          .eq('user_id', owner)
          .is('time_log_id', null)
          .gte('captured_at', startISO)
          .lte('captured_at', endISO)
          .order('captured_at')
          .order('id'),
      );
      if (legacy.length === 0) continue;

      // A midnight-spanning session can overlap one starting just outside the
      // report. Include that neighbor only when checking legacy ownership.
      const neighbors = await fetchCompleteEvidence<TimeLogForMerge>(
        supabase.from('time_logs')
          .select('id, user_id, start_time, end_time, status, deducted_seconds')
          .eq('user_id', owner)
          .lte('start_time', endISO)
          .or(`end_time.gte.${startISO},end_time.is.null`)
          .order('start_time')
          .order('id'),
      );
      const contextLogs = [...new Map([...neighbors, ...logs].map(log => [log.id, log])).values()];
      const legacyCaps = getLastScreenshotByTimeLogId(contextLogs, legacy, { nowMs });
      evidence.push(...legacy.filter(screenshot => missing.some(session =>
        session.user_id === screenshot.user_id
        && legacyCaps.get(session.id!) === new Date(screenshot.captured_at).getTime())));
    } catch {
      console.warn('[screenshot-evidence] Incomplete legacy ownership evidence; skipping fallback');
    }
  }
  return evidence;
}
